// CHAT-SWITCH-A (docs/direction/2026-10-28-chat-slack-switch.md): people's presence, custom
// status, local time and do-not-disturb in chat. One cached list (`chatStatus.list`, every minute
// and on focus) feeds the presence dots, the status emoji beside names and `MemberPresenceInfo`
// (the profile's lines). The viewer's browser time zone is saved once when it differs.
import { cn } from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { MoonIcon } from "lucide-react";
import { useEffect, useMemo, useRef } from "react";
import type { Api } from "../../api/client";
import { type ChatStatus, statusesQuery } from "./chat-queries";
import { PresenceDot, presenceState } from "./presence-dot";

export type StatusIndex = ReadonlyMap<string, ChatStatus>;

const EMPTY: StatusIndex = new Map();

/** Everyone's status the caller sees, by membership (empty until loaded, or for an operator). */
export function useStatuses(api: Api, co: string, enabled = true): StatusIndex {
  const q = useQuery({ ...statusesQuery(api, co), enabled });
  const data = q.data;
  return useMemo(
    () => (data ? new Map(data.data.map((s) => [s.membership_id, s])) : EMPTY),
    [data],
  );
}

/** One member's status (null when not known). */
export function useMemberStatus(
  chat: { api: Api; co: string; meId: string | null },
  membershipId: string | null | undefined,
): ChatStatus | null {
  const all = useStatuses(chat.api, chat.co, chat.meId !== null);
  return membershipId ? (all.get(membershipId) ?? null) : null;
}

/** The browser's IANA time zone (null when it cannot tell). */
export function browserTimeZone(): string | null {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch {
    return null;
  }
}

/** Saves the viewer's set status and keeps the cached list in step. */
export function useSetStatus(chat: { api: Api; co: string }) {
  const queryClient = useQueryClient();
  const key = statusesQuery(chat.api, chat.co).queryKey;
  return useMutation({
    mutationFn: (body: {
      emoji?: string | null;
      text?: string | null;
      expires_at?: string | null;
      time_zone?: string;
    }) => chat.api.client.chatStatus.set({ params: { co: chat.co }, body }),
    onSuccess: (mine) => {
      queryClient.setQueryData(key, (prev) =>
        prev
          ? {
              ...prev,
              data: prev.data.some((s) => s.membership_id === mine.membership_id)
                ? prev.data.map((s) => (s.membership_id === mine.membership_id ? mine : s))
                : [...prev.data, mine],
            }
          : prev,
      );
    },
  });
}

/** Once per load: when the browser's time zone differs from the viewer's stored one, save it. */
export function useTimeZoneSync(chat: { api: Api; co: string; meId: string | null }) {
  const all = useStatuses(chat.api, chat.co, chat.meId !== null);
  const mine = chat.meId ? all.get(chat.meId) : undefined;
  const set = useSetStatus(chat);
  const done = useRef(false);
  const { mutate } = set;
  useEffect(() => {
    if (done.current || !mine) return;
    done.current = true;
    const zone = browserTimeZone();
    if (zone && zone !== mine.time_zone) mutate({ time_zone: zone });
  }, [mine, mutate]);
}

/** "14:05" in `zone` (the viewer's clock style), or null for an unknown zone. */
export function localTime(zone: string, at: Date): string | null {
  try {
    return new Intl.DateTimeFormat(undefined, {
      timeZone: zone,
      hour: "2-digit",
      minute: "2-digit",
    }).format(at);
  } catch {
    return null;
  }
}

/** "GMT+2" for `zone` at `at` (null when the browser cannot say). */
export function zoneOffset(zone: string, at: Date): string | null {
  try {
    const part = new Intl.DateTimeFormat("en-US", { timeZone: zone, timeZoneName: "shortOffset" })
      .formatToParts(at)
      .find((p) => p.type === "timeZoneName");
    return part?.value ?? null;
  } catch {
    return null;
  }
}

/** "until 15:30" today, "until Fri 09:00" this week, else "until 3 Nov". */
export function untilLabel(expiresAt: string, now: Date): string {
  const end = new Date(expiresAt);
  const time = end.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  if (end.toDateString() === now.toDateString()) return `until ${time}`;
  if (end.getTime() - now.getTime() < 6 * 86_400_000)
    return `until ${end.toLocaleDateString(undefined, { weekday: "short" })} ${time}`;
  return `until ${end.toLocaleDateString(undefined, { day: "numeric", month: "short" })}`;
}

/** A person's status emoji beside their name, the text as its tooltip (nothing without one). */
export function StatusEmoji({
  status,
  className,
}: {
  status: Pick<ChatStatus, "emoji" | "text"> | null | undefined;
  className?: string;
}) {
  if (!status?.emoji && !status?.text) return null;
  const label = status.text ?? "Status";
  return (
    <span
      role="img"
      aria-label={`Status: ${label}`}
      title={status.text ?? undefined}
      data-slot="status-emoji"
      className={cn("shrink-0 text-[13px] leading-none", className)}
    >
      {status.emoji ?? "💬"}
    </span>
  );
}

/**
 * A person's presence lines (the profile's; CHAT-SWITCH-A): status emoji, text and "until …",
 * Active / Away, local time in their zone with its offset, and "Do not disturb". `inline` puts
 * them on one muted line (a header).
 */
export function MemberPresenceInfo({
  status,
  now = new Date(),
  inline = false,
  className,
}: {
  status: ChatStatus | null | undefined;
  now?: Date;
  inline?: boolean;
  className?: string;
}) {
  if (!status) return null;
  const custom = status.emoji || status.text;
  const time = status.time_zone ? localTime(status.time_zone, now) : null;
  const offset = status.time_zone ? zoneOffset(status.time_zone, now) : null;
  const state = presenceState(status);
  const items = [
    custom ? (
      <span
        key="status"
        data-slot="presence-status"
        className="inline-flex min-w-0 items-center gap-1"
      >
        {status.emoji ? <span aria-hidden="true">{status.emoji}</span> : null}
        {status.text ? <span className="truncate text-foreground">{status.text}</span> : null}
        {status.expires_at ? (
          <span className="text-muted-foreground">{untilLabel(status.expires_at, now)}</span>
        ) : null}
      </span>
    ) : null,
    <span key="presence" data-slot="presence-state" className="inline-flex items-center gap-1.5">
      <PresenceDot state={status.presence} className="ring-0" />
      {status.presence === "active" ? "Active" : "Away"}
    </span>,
    status.dnd ? (
      <span key="dnd" data-slot="presence-dnd" className="inline-flex items-center gap-1">
        <MoonIcon aria-hidden="true" className="size-3" />
        Do not disturb
      </span>
    ) : null,
    time ? (
      <span key="time" data-slot="presence-time" title={status.time_zone ?? undefined}>
        Local time {time}
        {offset ? ` (${offset})` : ""}
      </span>
    ) : null,
  ].filter(Boolean);
  return (
    <div
      data-slot="member-presence-info"
      data-state={state}
      className={cn(
        "text-xs text-muted-foreground",
        inline
          ? "flex min-w-0 items-center gap-x-2 gap-y-0.5 overflow-hidden whitespace-nowrap [&>*+*]:before:mr-2 [&>*+*]:before:text-faint [&>*+*]:before:content-['·']"
          : "flex flex-col gap-1",
        className,
      )}
    >
      {items}
    </div>
  );
}
